import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, FileText, Users, Handshake } from "lucide-react";

import Header from "../components/Header";
import { usePromoStatus } from "../hooks/usePromoStatus";
import { formatDateShort } from "../lib/currency";

/**
 * Modern B2B SaaS homepage — Talent Junction v2.
 * Sections: hero (dot grid bg, trusted logos) → manifesto (dark) → 3 steps → for-who → final CTA → footer.
 */
export default function Home() {
  const { t } = useTranslation();
  const promo = usePromoStatus();
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="home-page">
      <Header />

      {/* ========== PROMO STRIP (top, above hero) ========== */}
      {promo && promo.active && (
        <div
          className="relative border-b border-indigo-700 bg-gradient-to-r from-indigo-700 via-indigo-600 to-violet-600 text-white"
          data-testid="home-promo-banner"
        >
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-6 py-3 sm:flex-row sm:items-center">
            <p className="text-sm font-medium">
              <span className="mr-2">🌞</span>
              <span className="font-semibold">{t("promo.home_title")}</span>{" "}
              <span className="opacity-90">— {t("promo.home_body", { date: formatDateShort(promo.end_date) })}</span>
            </p>
            <Link
              to="/signup/agency"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/95 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-white"
              data-testid="home-promo-cta"
            >
              {t("promo.home_cta")}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      )}

      <main>
        {/* ========== HERO ========== */}
        <section className="relative overflow-hidden">
          {/* Fine dot grid pattern for subtle texture */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(rgba(15,23,42,0.07) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)",
            }}
          />
          <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-24 sm:pt-32">
            <img
              src="/Logo.png"
              alt=""
              className="mb-6 h-16 w-16 rounded-2xl object-cover shadow-sm"
              data-testid="hero-logo"
            />
            <span
              className="text-xs font-semibold uppercase tracking-widest text-slate-500"
              data-testid="hero-eyebrow"
            >
              // {t("home_v2.eyebrow")}
            </span>

            <h1
              className="mt-6 max-w-4xl text-5xl font-semibold leading-[1.05] tracking-tighter text-slate-900 md:text-7xl"
              data-testid="hero-title"
            >
              {t("home_v2.hero_title_a")}
              <span className="text-indigo-600">
                {t("home_v2.hero_title_accent")}
              </span>
              {t("home_v2.hero_title_b")}
            </h1>

            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600 md:text-xl">
              {t("home_v2.hero_subtitle")}
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              {t("home_v2.hero_p2")}
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              {t("home_v2.hero_p3")}
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/signup/employer"
                className="group inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-700"
                data-testid="hero-cta-employer"
              >
                {t("home_v2.cta_employer")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
              </Link>
              <Link
                to="/signup/agency"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
                data-testid="hero-cta-agency"
              >
                {t("home_v2.cta_agency")}
              </Link>
            </div>

            {/* Trusted by logos row */}
            <div className="mt-16">
              <p className="text-xs font-medium uppercase tracking-widest text-slate-500" data-testid="trusted-by-label">
                {t("home_v2.trusted_by")}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-10 gap-y-4">
                {["Novavia", "Cortalys", "Meridien", "Aurora", "Sequoia"].map((name) => (
                  <BrandLogo key={name} name={name} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========== MANIFESTO (dark) ========== */}
        <section className="border-y border-slate-900/10 bg-slate-900 text-white" data-testid="manifesto-section">
          <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
            <p className="text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              {t("home_v2.manifesto_1")}
              <span className="text-indigo-400">{t("home_v2.manifesto_1_accent")}</span>
              {t("home_v2.manifesto_1_suffix")}
            </p>
            <p className="mt-4 text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              {t("home_v2.manifesto_2")}
              <span className="text-indigo-400">{t("home_v2.manifesto_2_accent")}</span>
              {t("home_v2.manifesto_2_suffix")}
            </p>
            <p className="mt-4 text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              {t("home_v2.manifesto_3")}
              <span className="text-indigo-400">{t("home_v2.manifesto_3_accent")}</span>
              {t("home_v2.manifesto_3_suffix")}
            </p>
          </div>
        </section>

        {/* ========== 3 STEPS ========== */}
        <section className="bg-white" data-testid="steps-section">
          <div className="mx-auto max-w-6xl px-6 py-24">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">// {t("home_v2.steps_subtitle")}</p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-tighter text-slate-900 md:text-5xl">
                {t("home_v2.steps_title")}
              </h2>
            </div>

            <div className="mt-14 grid gap-10 md:grid-cols-3">
              <StepCard n="01" Icon={FileText} title={t("home_v2.step_1_title")} body={t("home_v2.step_1_body")} testId="step-card-1" />
              <StepCard n="02" Icon={Users} title={t("home_v2.step_2_title")} body={t("home_v2.step_2_body")} testId="step-card-2" />
              <StepCard n="03" Icon={Handshake} title={t("home_v2.step_3_title")} body={t("home_v2.step_3_body")} testId="step-card-3" />
            </div>
          </div>
        </section>

        {/* ========== FOR WHO ========== */}
        <section className="bg-slate-50 border-t border-slate-200/70" data-testid="who-section">
          <div className="mx-auto max-w-6xl px-6 py-24">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">// {t("home_v2.who_title")}</p>
            <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-tighter text-slate-900 md:text-5xl">
              {t("home_v2.who_title")}
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <WhoCard
                title={t("home_v2.who_emp_title")}
                body={t("home_v2.who_emp_body")}
                cta={t("home_v2.who_emp_cta")}
                to="/signup/employer"
                testId="who-employer"
                variant="employer"
              />
              <WhoCard
                title={t("home_v2.who_esn_title")}
                body={t("home_v2.who_esn_body")}
                cta={t("home_v2.who_esn_cta")}
                to="/signup/agency"
                testId="who-agency"
                variant="agency"
              />
            </div>
          </div>
        </section>

        {/* ========== FINAL CTA ========== */}
        <section className="bg-indigo-600 text-white" data-testid="final-cta-section">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
                  {t("home_v2.final_cta_title")}
                </h2>
                <p className="mt-3 max-w-xl text-base text-indigo-100 md:text-lg">
                  {t("home_v2.final_cta_body")}
                </p>
              </div>
              <Link
                to="/signup/employer"
                className="group inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-medium text-indigo-700 shadow-sm transition-colors hover:bg-slate-50"
                data-testid="final-cta-btn"
              >
                {t("home_v2.final_cta_btn")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ========== FOOTER (4 col + language + copyright) ========== */}
      <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="grid gap-10 md:grid-cols-6">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2">
                <img src="/Logo.png" alt="Talent Junction" className="h-8 w-8 rounded-lg object-cover" />
                <span className="text-base font-semibold tracking-tight text-slate-900">{t("brand")}</span>
              </div>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-600">
                {t("home.footer_tagline")}
              </p>
            </div>
            <FooterCol title={t("home_v2.footer_col_product")}>
              <Link to="/login" className="hover:text-slate-900" data-testid="footer-link-marketplace">{t("home_v2.footer_link_marketplace")}</Link>
              <Link to="/login" className="hover:text-slate-900" data-testid="footer-link-cvtheque">{t("home_v2.footer_link_cvtheque")}</Link>
              <span className="cursor-not-allowed" data-testid="footer-link-pricing">{t("home_v2.footer_link_pricing")}</span>
            </FooterCol>
            <FooterCol title={t("home_v2.footer_col_resources")}>
              <span className="cursor-not-allowed" data-testid="footer-link-docs">{t("home_v2.footer_link_docs")}</span>
              <span className="cursor-not-allowed" data-testid="footer-link-faq">{t("home_v2.footer_link_faq")}</span>
            </FooterCol>
            <FooterCol title={t("home_v2.footer_col_legal")}>
              <Link to="/legal/terms" className="hover:text-slate-900" data-testid="footer-cgv">{t("home_v2.footer_link_cgv")}</Link>
              <Link to="/legal/privacy" className="hover:text-slate-900" data-testid="footer-link-privacy">{t("home_v2.footer_link_privacy")}</Link>
              <Link to="/legal/cookies" className="hover:text-slate-900" data-testid="footer-link-cookies">{t("home_v2.footer_link_cookies")}</Link>
            </FooterCol>
            <FooterCol title={t("home_v2.footer_col_contact")}>
              <a href={`mailto:${t("home_v2.footer_contact_email")}`} className="hover:text-slate-900" data-testid="footer-contact-email">
                {t("home_v2.footer_contact_email")}
              </a>
            </FooterCol>
          </div>

          <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-6 md:flex-row md:items-center">
            <p className="text-xs text-slate-500" data-testid="footer-copyright">
              {t("home_v2.footer_copyright", { year })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------- sub-components -------- */

function StepCard({ n, Icon, title, body, testId }) {
  return (
    <div className="group" data-testid={testId}>
      <div className="flex items-baseline gap-4 border-t border-slate-200 pt-6">
        <span className="text-4xl font-semibold tracking-tighter text-slate-300">{n}</span>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        </span>
      </div>
      <h3 className="mt-6 text-xl font-semibold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function WhoCard({ title, body, cta, to, testId, variant }) {
  const chip =
    variant === "employer"
      ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100"
      : "bg-teal-50 text-teal-700 ring-1 ring-teal-100";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid={testId}>
      <span className={"inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest " + chip}>
        {title}
      </span>
      <p className="mt-5 text-base leading-relaxed text-slate-700 md:text-lg">
        {body}
      </p>
      <Link
        to={to}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-indigo-700 hover:text-indigo-800"
        data-testid={`${testId}-cta`}
      >
        {cta}
        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
      </Link>
    </div>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-900">{title}</h4>
      <div className="mt-4 flex flex-col gap-2 text-sm text-slate-600">
        {children}
      </div>
    </div>
  );
}

/**
 * BrandLogo — inline SVG "wordmark" placeholder in grayscale. Not a real logo,
 * mimics the "trusted by" row à la Stripe.
 */
function BrandLogo({ name }) {
  return (
    <span
      className="text-lg font-semibold tracking-tight text-slate-400 grayscale opacity-70 hover:opacity-100 transition-opacity"
      style={{ fontFamily: "'Inter', sans-serif", letterSpacing: "-0.02em" }}
      data-testid={`brand-logo-${name.toLowerCase()}`}
    >
      {name}
    </span>
  );
}
